import { Link, styled, TableCell, TableRow } from "@mui/material";
import { GeoJSON } from "leaflet";
import { useTranslation } from "react-i18next";
import { floor } from "lodash-es";
import { area } from "@turf/turf";

import {
  getGeojsonFeature,
  resetPolygonLayerStyle,
  applyDeleteLayerStyle,
} from "utils/GeomanUtils";

import {
  DrawType,
  Measure,
  MeasureConfLayer,
  MeasureConf,
  DrawState,
} from "types/types";

import { EditPolygon } from "./EditPolygon";

interface Props {
  index: number;
  basePolygon: GeoJSON;
  measure: Measure;
  measureConfigurations: MeasureConf[];
  layer: MeasureConfLayer;
  minArea: number;
  drawState: DrawState;
  removeLayerFromMeasureConfig: (layerId: number) => void;
  updateMeasureConfigurations: (updatedMeasureConfigs: MeasureConf[]) => void;
  updateDrawState: (drawType?: DrawType) => void;
}

export function ActiveMeasureConfigPolyItem(props: Props) {
  const {
    index,
    layer,
    basePolygon,
    measureConfigurations,
    measure,
    minArea,
    drawState,
    removeLayerFromMeasureConfig,
    updateMeasureConfigurations,
    updateDrawState,
  } = props;
  const { t } = useTranslation();

  function deleteMeasureConfig() {
    removeLayerFromMeasureConfig(layer.id);
    // After deleting a layer the draw listener of geoman will still have the old state
    // By changing the UUID of Draw state we disable and re-enable the draw listener
    // This will make sure that the draw listener is in sync with the current state
    updateDrawState();
    layer.geometryLayer.remove();
  }

  return (
    <StyledTableRow>
      <TableCell>{index + 1}</TableCell>
      <TableCell className="no-wrap">
        {t("label.measureWithNumber", {
          measure: measure.name,
          number: index + 1,
        })}
      </TableCell>
      <TableCell align="left">
        {t("label.areaSqM", {
          area: floor(
            area(getGeojsonFeature(layer.geometryLayer)),
            2
          ).toLocaleString("nl-BE"),
        })}
      </TableCell>
      <TableCell className="flex-cell">
        <Link
          onMouseOver={() => applyDeleteLayerStyle(layer.geometryLayer)}
          onMouseLeave={() =>
            resetPolygonLayerStyle(layer.geometryLayer, measure)
          }
          onClick={deleteMeasureConfig}
          className={`align-right ${
            drawState.drawType === "edit" ? "disabled" : "enabled"
          }`}
        >
          {t("label.delete")}
        </Link>
        <EditPolygon
          layer={layer}
          basePolygon={basePolygon}
          measureConfigurations={measureConfigurations}
          measure={measure}
          minArea={minArea}
          drawState={drawState}
          updateDrawState={updateDrawState}
          updateMeasureConfigurations={updateMeasureConfigurations}
        />
      </TableCell>
    </StyledTableRow>
  );
}

const StyledTableRow = styled(TableRow)`
  .MuiTableCell-root {
    padding: ${({ theme }) => theme.spacing(1)};
    border: unset;
  }

  .flex-cell {
    display: flex;
    justify-content: flex-end;
    gap: ${({ theme }) => theme.spacing(2)};
  }
  .no-wrap {
    white-space: nowrap;
    text-overflow: ellipsis;
  }
`;
